/**
 * Price input cell component
 *
 * Custom cell for ag-grid that shows a price input.
 */
import {
  AbstractType,
  AfterViewInit,
  Component,
  ElementRef,
  InjectionToken,
  Injector,
  OnDestroy,
  Type,
  ViewChild,
} from '@angular/core';
import { AgRendererComponent } from 'ag-grid-angular';
import { ICellRendererParams, GridApi } from 'ag-grid-community';
import {
  get,
  values,
  isFunction,
  isNumber,
  isEmpty,
  first,
  isNil,
  includes,
  isInteger,
} from 'lodash';

import {
  KEY_CODES,
  DEFAULT_INPUT_THOUSANDS_DIVIDER,
  BLOCK_NAVIGATION_KEY_CODES,
  DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
} from '@config/global-constants';
import {
  XUX_GRID_ACTIONS,
  XuxGridAction,
  GridValueChangeFunc,
  CUSTOM_XUX_GRID_ACTIONS,
  IInputPreferenceDecimalPrecision,
  EInputEvent,
  IArtificialInputWatcherOption,
} from '@shared/types';
import { ScanningService, StateService } from '@shared/services';
import { generateDecimalRegexp } from '@app/shared/constants';
import { getValueForPinnedCell } from '../renderer-helpers';
import { lazyInject } from '@containers/base/decorators/lazy-inject.decorator';
import { NumberFormatService } from '@shared/services/number-format';
import {
  generateMaximumByPreferencesPrecision,
  removeInvalidZeros,
} from '@app/shared/utils/number-utils';
import { floatNumberRoundingFormat } from '@app/shared/utils/rounding-utils';
import { decimalNumber } from '@shared/common';
import { setFocusedCell } from '@app/components/xvi-grid/decorators/set-focused-cell.decorator';
import { artificialInputWatcher, judgeScanned } from '@app/shared/utils';
import { Subject } from '@app/shared/rx';

const NEGATIVE_SIGN: string = '-';
const EMPTY_VALUES: any[] = ['', null, undefined];

const START_WITH_ZERO_NUMBER_REGEX: RegExp = new RegExp('^0[0-9]');

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-float-input-renderer',
  templateUrl: 'float-input-renderer.component.html',
  styleUrls: ['float-input-renderer.component.scss'],
})
export class XVIFloatInputRendererComponent
  implements AgRendererComponent, AfterViewInit, OnDestroy
{
  @ViewChild('inputElement', { static: false }) public inputElement: ElementRef;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(ScanningService) public scanningService: ScanningService;

  public lastValue: number | string;
  public value: number | string = 0;
  public valueBeforeEdit: number | string = 0;
  public placeholder: string = '';
  public type: 'text' | 'number' = 'text';
  public class: string = '';
  public disabled: boolean = false;
  public emitItemAction: Function;
  public params: ICellRendererParams;
  public precision: number;
  public pinnedRowValueMapper: Function = getValueForPinnedCell;
  protected lastEmittedValue: number | string = null;
  protected artificialInputCheck:
    | {
        artificialInputThreshold?: number;
      }
    | boolean = false;

  protected get artificialInputThreshold() {
    return get(
      this.artificialInputCheck,
      'artificialInputThreshold',
      DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
    );
  }

  public disableKeyUpFinishEditing: boolean;
  public shouldLockExitEditingMode: boolean = false;
  public isEnterKeyPress: boolean = true;
  public keyTimes: number[] = [];

  public cancelSubscription$ = new Subject<void>();

  public max: number;
  public min: number;
  public usePreferenceDecimalPrecision: IInputPreferenceDecimalPrecision = {
    overrideMax: true,
    overrideMin: true,
  };
  public keepValidZeros: number = 0;

  public blurWhenToNext: boolean = false;
  /**
   * use this to prevent onFocusOut trigger finishEditing again after finishEditing called by enter key
   */
  public hasCalledFinishEditing: boolean = false;

  /**
   * whether to check change with valueBeforeEdit instead of lastEmittedValue while finish editing
   * consider to combine with lastEmittedValue?
   */
  public compareChangeWithValueBeforeEdit: boolean = false;

  /**
   * will replace to '' which match regex when key up
   */
  public acceptableInputRegex: RegExp;

  public onValueChange: GridValueChangeFunc;

  /**
   * min precision setting
   */
  protected minDecimalPlacesToCheck?: number;

  public constructor(
    public injector: Injector,
    public stateService: StateService,
  ) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public agInit(params: ICellRendererParams): void {
    const precision = get(params, 'precision');

    const defaultPrecision = this.stateService.getNumberPrecision();

    this.precision = isNaN(precision) ? defaultPrecision : precision;
    this.params = params;
    this.artificialInputCheck = get(
      params,
      'artificialInputCheck',
      this.artificialInputCheck,
    );
    this.pinnedRowValueMapper = get(
      params,
      'pinnedRowValueMapper',
      this.pinnedRowValueMapper,
    );
    this.placeholder = get(params, 'placeholder', '');
    this.refresh(params);

    this.valueBeforeEdit = this.value;
    this.type = get(params, 'type', 'text');
    this.class = get(params, 'class', '');
    this.disabled = get(this.params, 'disabled', null);
    this.emitItemAction = get(this.params, 'context.emitItemAction');
    this.lastEmittedValue = this.value;
    // assign last value for the first time
    this.lastValue = this.value;

    this.blurWhenToNext = get(params, 'blurWhenToNext', this.blurWhenToNext);
    this.compareChangeWithValueBeforeEdit = get(
      params,
      'compareChangeWithValueBeforeEdit',
      this.compareChangeWithValueBeforeEdit,
    );
    this.disableKeyUpFinishEditing = get(
      params,
      'disableKeyUpFinishEditing',
      false,
    );
    this.usePreferenceDecimalPrecision = get(
      params,
      'usePreferenceDecimalPrecision',
      {
        overrideMin: true,
        overrideMax: true,
      },
    );

    const receivedMin = get(params, 'min', null);
    const receivedMax = get(params, 'max', null);
    this.min = this.usePreferenceDecimalPrecision.overrideMin
      ? generateMaximumByPreferencesPrecision(
          parseInt(String(receivedMin), 10),
          this.precision,
        )
      : receivedMin;

    this.max = this.usePreferenceDecimalPrecision.overrideMax
      ? generateMaximumByPreferencesPrecision(
          parseInt(String(receivedMax), 10),
          this.precision,
        )
      : receivedMax;

    // if user don`t input acceptableInputRegex, then here will only default generate a regex about positive number.
    // so, if user wanna use regex from negative to positive, user need input acceptableInputRegex.
    this.acceptableInputRegex = get(
      params,
      'acceptableInputRegex',
      generateDecimalRegexp(this.precision),
    );

    this.onValueChange = get(params, 'onValueChange', null);
  }

  // eslint-disable-next-line
  public refresh(params: ICellRendererParams): boolean {
    // This should be done for every renderer
    // Either we digest new value in this component or
    // need to tell the grid to replace this component by returning "false"
    // Then grid will destroy this component and crete a new one, obviously running agInit.
    if (this.getNumberValue(params.value) === this.getNumberValue(this.value)) {
      return true;
    }

    if (get(params, 'node.rowPinned')) {
      const gridApi: GridApi = get(this.params, 'api.gridCore.gridApi');
      if (gridApi) {
        this.calculatePinnedValue(this.pinnedRowValueMapper(params, gridApi));
      }

      return true;
    }

    const defaultValue = get(params, 'defaultValue', 0);
    const paramValue = get(params, 'value', defaultValue);
    this.value = isNaN(paramValue) ? '' : this.getNumberValue(paramValue, true);
    // assign this value to lastValue to keep value with init status when refresh
    this.lastValue = this.value;

    this.calculatePlaceholder(params);

    return true;
  }

  public ngAfterViewInit(): void {
    if (this.artificialInputCheck) {
      const artificialInputWatcherOption: IArtificialInputWatcherOption = {
        nativeElement: this.inputElement.nativeElement,
        cancelSubscription$: this.cancelSubscription$,
        artificialInputThreshold: this.artificialInputThreshold,
      };

      artificialInputWatcher(
        EInputEvent.KEYDOWN,
        {
          ...artificialInputWatcherOption,
          eventHandler: this.onKeydown.bind(this),
          updateValueBeforeEventHandler: true,
          passByKeyList: [KEY_CODES.TAB, KEY_CODES.CLEAR],
          scanningService: this.scanningService,
        },
        this.setNewValue.bind(this),
        this.inputCheckValid.bind(this),
      );

      artificialInputWatcher(
        EInputEvent.KEYUP,
        {
          ...artificialInputWatcherOption,
          passByKeyList: [KEY_CODES.ENTER, KEY_CODES.CLEAR],
          eventHandler: this.onKeyup.bind(this),
          scanningService: this.scanningService,
        },
        this.setNewValue.bind(this),
      );
    }
  }

  /**
   * calculatePlaceholder
   * will calculate placeholder by params
   *
   * @param params
   */
  public calculatePlaceholder(params: ICellRendererParams): void {
    const placeholderFunc = get(params, 'placeholderFunc');
    if (isFunction(placeholderFunc)) {
      // to keep consistent with ag-grid value in params
      this.placeholder = placeholderFunc({ ...params, value: this.value });
    }
  }

  public calculatePinnedValue(value: string): void {
    const showRangeInPlaceholder = get(
      this.params,
      'showRangeInPlaceholder',
      false,
    );

    if (showRangeInPlaceholder) {
      const isRange = value.includes('-');

      this.value = isRange ? null : Number(value);
      this.placeholder = isRange ? value : get(this.params, 'placeholder', '');
    } else {
      this.value = value ? Number(value) : value;
    }
  }

  public isStartZeroLengthLimitExceeded(value = this.value): boolean {
    const newValue = String(value);

    return START_WITH_ZERO_NUMBER_REGEX.test(newValue);
  }

  public isFractionLengthLimitExceeded(value = this.value): boolean {
    const newValue = String(value);
    const [, fraction] = newValue.split('.');
    const fractionSymbolsCount = get(fraction, 'length', 0);

    return fractionSymbolsCount > this.precision;
  }

  public ensureValueAcceptable(value = this.value): void {
    if (isNil(this.acceptableInputRegex)) {
      return;
    }
    // reset lastIndex for next exec
    this.acceptableInputRegex.lastIndex = 0;
    const regExpExecuteResult = first(
      this.acceptableInputRegex.exec(value?.toString()),
    );
    this.value = !isEmpty(regExpExecuteResult) ? regExpExecuteResult : null;
  }

  public ensureValueIsBetweenMinAndMax(
    value = this.value,
    lastValue: number | string = this.lastValue,
  ): number | string {
    let newValue = value;
    const allowEmptyValue = get(this.params, 'allowEmptyValue', false);
    const forceConversionOfZeroToEmpty = get(
      this.params,
      'forceConversionOfZeroToEmpty',
      false,
    );

    if (
      (allowEmptyValue && isNil(value)) ||
      (forceConversionOfZeroToEmpty && decimalNumber(value).eq(0))
    ) {
      this.value = null;

      return null;
    }

    if (!isNil(this.min) && newValue < this.min) {
      // use min value if lastValue is null.
      newValue = isNil(lastValue) ? this.min : lastValue;
    }

    if (!isNil(this.max) && newValue > this.max) {
      newValue = lastValue;
    }

    this.value = newValue;

    return newValue;
  }

  public onKeyup(
    event: KeyboardEvent,
    skipArtificialInputCheck: boolean = false,
  ): void {
    if (!skipArtificialInputCheck && this.artificialInputCheck) {
      return;
    }
    this.ensureValueAcceptable();
    this.ensureValueIsBetweenMinAndMax();
    this.calculatePlaceholder(this.params);

    this.lastValue = this.value;
    if (
      this.isStartZeroLengthLimitExceeded() ||
      this.isFractionLengthLimitExceeded()
    ) {
      this.value = this.getNumberValue(this.value);

      return;
    }

    const pinnedRowPosition = get(this.params, 'node.rowPinned');

    if (
      (!this.disableKeyUpFinishEditing && !pinnedRowPosition) ||
      event.key === KEY_CODES.ENTER
    ) {
      this.isEnterKeyPress = true;
      this.finishEditing();
    }

    if (
      includes(
        [
          KEY_CODES.ARROW_UP,
          KEY_CODES.ARROW_DOWN,
          KEY_CODES.ARROW_LEFT,
          KEY_CODES.ARROW_RIGHT,
        ],
        event.key,
      )
    ) {
      this.isEnterKeyPress = false;
    }

    if (event.key === KEY_CODES.ESCAPE) {
      this.emitExitEditingAction(false, false);
    }
  }

  public finishEditing(navigateToNextRow: boolean = true): void {
    if (this.blurWhenToNext && navigateToNextRow) {
      // assign hasCalledFinishEditing to true to prevent onFocusOut trigger finishEditing again
      this.hasCalledFinishEditing = true;

      // this line is used to force the input to lose focus(for keep data in xux-form-list), so that the grid can exit editing mode
      this.inputElement.nativeElement.blur();
    }

    const isValueChange = this.isValueChanged();
    if (!isValueChange) {
      this.emitExitEditingAction(true, navigateToNextRow);

      return;
    }

    const action = this.createAction();

    let isStillCallEmit = true;
    if (isFunction(this.onValueChange) && isValueChange) {
      this.lastEmittedValue = this.value;
      isStillCallEmit = this.onValueChange(action.payload as any);
    }

    if (isStillCallEmit && isFunction(this.emitItemAction) && isValueChange) {
      this.lastEmittedValue = this.value;
      this.emitItemAction(action);
    }
  }

  public ngOnDestroy(): void {
    this.keyTimes = [];
    this.cancelSubscription$.next();
  }

  public onFocusOut(): void {
    this.ensureValueAcceptable();
    this.ensureValueIsBetweenMinAndMax();
    this.calculatePlaceholder(this.params);
    if (this.value === NEGATIVE_SIGN) this.value = 0;
    // Because of the comparison between '0' and 0, null and undefined,
    // triple-equals is not used here deliberately
    // eslint-disable-next-line eqeqeq
    if (!this.hasCalledFinishEditing && this.value != this.valueBeforeEdit) {
      this.finishEditing(this.isEnterKeyPress);
    }

    if (this.hasCalledFinishEditing) {
      // reset hasCalledFinishEditing after finishEditing called by enter key
      this.hasCalledFinishEditing = false;
    }
  }

  @setFocusedCell()
  public onFocusIn(event: FocusEvent): void {
    this.valueBeforeEdit = this.value;
    (event.target as HTMLInputElement).select();
  }

  public onGridCustomFocus(event: Event): void {
    // Added custom event to focus input with enter key in grid,
    // which will trigger a keyup event, but there is no input at this time,
    // so you need to use a lock and ignore it when the keyup event is triggered for the first time
    this.shouldLockExitEditingMode = true;
    (event.target as HTMLInputElement).focus();
  }

  public onKeydown(
    event: KeyboardEvent,
    skipArtificialInputCheck: boolean = false,
  ): void {
    if (!skipArtificialInputCheck) {
      this.keyTimes.push(Date.now());
    }

    if (
      judgeScanned(this.keyTimes) &&
      includes([KEY_CODES.TAB, KEY_CODES.ENTER], event.key)
    ) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      return;
    }

    if (!skipArtificialInputCheck && this.artificialInputCheck) {
      return;
    }
    // eslint-disable-next-line import/no-deprecated
    const keyCode: number = event.keyCode;
    const isBlockNavigationKey: boolean = values(
      BLOCK_NAVIGATION_KEY_CODES,
    ).includes(keyCode);

    if (isBlockNavigationKey) {
      event.stopPropagation();
    }

    if (
      includes(
        [
          KEY_CODES.TAB,
          KEY_CODES.SHIFT,
          KEY_CODES.ARROW_UP,
          KEY_CODES.ARROW_DOWN,
          KEY_CODES.ARROW_LEFT,
          KEY_CODES.ARROW_RIGHT,
        ],
        event.key,
      )
    ) {
      // if you handle input cursor position automatically, please comment this line
      // and should remove this when use keyboard navigation directive, because we will handle this in directive,
      // otherwise, it will cause issue when user use arrow key to move cursor in input but the event is triggered by directive and cause unexpected navigation
      event.preventDefault();
    }

    // Allow only numeric symbols
    if (event?.key?.length === 1 && !this.inputCheckValid(event?.key)) {
      event.preventDefault();
    }

    if (event.key === KEY_CODES.ESCAPE) {
      // revert value
      this.value = this.valueBeforeEdit;
    }
  }

  public createAction(): XuxGridAction {
    let type = XUX_GRID_ACTIONS.ITEM_DATA_CHANGED;
    const pinnedRowPosition = get(this.params, 'node.rowPinned');

    if (pinnedRowPosition === 'top') {
      type = XUX_GRID_ACTIONS.PINNED_TOP_ITEM_DATA_CHANGED;
    }

    const field: string = get(this.params, 'column.colDef.field', '');
    const rowIndex: number = get(this.params, 'node.rowIndex', 0);
    const rowId: string = get(this.params, 'node.id', null);
    const row: object = get(this.params, 'node.data');
    const value = this.getNumberValue(this.value);

    const action: XuxGridAction = {
      type,
      payload: {
        field,
        rowIndex,
        rowId,
        value,
        rowData: [row],
        rowNode: this.params.node,
      },
    };

    return action;
  }

  public setNewValue(
    event: KeyboardEvent,
    artificialInputWatcherOption: IArtificialInputWatcherOption,
    newValue: string,
  ): void {
    const { updateValueBeforeEventHandler } = artificialInputWatcherOption;
    const eventKey = event?.key;

    if (updateValueBeforeEventHandler && this.inputCheckValid(eventKey)) {
      this.value = newValue;
    }

    if (
      get(artificialInputWatcherOption, 'isValidScanning') &&
      this.isValueChanged()
    ) {
      const action = this.createAction();
      this.emitItemAction(action);
    }
  }

  protected getNumberValue(
    input: string | number,
    isInitStatus: boolean = false,
  ): number | string {
    if (EMPTY_VALUES.includes(input)) return null;
    if (input === NEGATIVE_SIGN) return 0;
    let inputValue = input;
    // isInteger: for received int 12, displayed as 12 without use format to format it as 12.00;
    if (!isInitStatus || isInteger(Number(input))) {
      inputValue = floatNumberRoundingFormat(input, {
        precision: this.precision,
        thousandsSeparator: DEFAULT_INPUT_THOUSANDS_DIVIDER,
        minDecimalPlacesToCheck: this.minDecimalPlacesToCheck,
      });
    }

    const preservedInputValue = removeInvalidZeros(
      String(inputValue),
      this.keepValidZeros,
    );

    return preservedInputValue;
  }

  private emitExitEditingAction(
    needValidationLock = false,
    navigateToNextRow = true,
  ): void {
    if (needValidationLock && this.shouldLockExitEditingMode) {
      this.shouldLockExitEditingMode = false;

      return;
    }
    const exitEditingAction = {
      type: CUSTOM_XUX_GRID_ACTIONS.EXIT_EDITING_MODE,
      payload: {
        navigateToNextRow,
      },
    };
    this.value = this.valueBeforeEdit;
    this.lastEmittedValue = this.valueBeforeEdit;
    this.emitItemAction(exitEditingAction);
  }

  private isValueChanged(): boolean {
    const compareValue = this.compareChangeWithValueBeforeEdit
      ? this.valueBeforeEdit
      : this.lastEmittedValue;

    const changed =
      parseFloat(this.value as string) !== parseFloat(compareValue as string);
    const bothEmpty =
      isEmpty(this.value) &&
      !isNumber(this.value) &&
      isEmpty(compareValue) &&
      !isNumber(compareValue);

    return !bothEmpty && changed;
  }

  private inputCheckValid(key: string): boolean {
    return /^[-|\d|\.]$/.test(key);
  }
}
